import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBooking } from '../context/BookingContext';
import DatePicker from './DatePicker';
import { apiService } from '../services/apiService';
import CustomDatePicker from './CustomDatePicker';


const SearchBar = () => {
  const { state, dispatch } = useBooking();
  
  
  // ==========================================================
  // --- MOBILE UI STATE ---
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false); // Level 1: Main Mobile Modal
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);   // Level 2: Destination Search View
  const [isGuestSheetOpen, setIsGuestSheetOpen] = useState(false);     // Level 2: Guest Bottom Sheet
  // ==========================================================

  // Desktop Toggles
  const [hotelDropdownOpen, setHotelDropdownOpen] = useState(false);
  const [guestsDropdownOpen, setGuestsDropdownOpen] = useState(false);
  const [inventoryStatus, setInventoryStatus] = useState({});

  // Data State
  const [hotelSearch, setHotelSearch] = useState('');
  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // --- PROMO CODE STATE ---
const [isPromoOpen, setIsPromoOpen] = useState(false);
const [promoInput, setPromoInput] = useState(state.searchState.promo_code || '');
const [promoLoading, setPromoLoading] = useState(false);
const [promoError, setPromoError] = useState('');
const [promoApplied, setPromoApplied] = useState(!!state.searchState.promo_code);
const promoRef = useRef(null);
// Add this state near your other state declarations (around line 30-40)
const [promoAppliedBeforeCart, setPromoAppliedBeforeCart] = useState(false);
const [currentPromoDiscountId, setCurrentPromoDiscountId] = useState(null);

// Add this effect to track when promo was applied before cart items
useEffect(() => {
  // If promo gets applied AND there are no cart items yet
  if (promoApplied && state.cart.length === 0 && !promoAppliedBeforeCart) {
    console.log('📝 Promo applied before any cart items - will clear cart on promo removal');
    setPromoAppliedBeforeCart(true);
  }
  
  // If cart gets items after promo was applied, keep the flag
  // If promo is removed, reset the flag
  if (!promoApplied && promoAppliedBeforeCart) {
    console.log('🔄 Resetting promoAppliedBeforeCart flag');
    setPromoAppliedBeforeCart(false);
  }
}, [promoApplied, state.cart.length, promoAppliedBeforeCart]);

// Add this with your other state declarations (around line 30-40)
const [hotelStackableInfo, setHotelStackableInfo] = useState({ 
  is_stackable: false, 
  available_promo_codes: [] 
});

 // Flag to track if widget promo has been applied
  const [widgetPromoApplied, setWidgetPromoApplied] = useState(false);
  const [justApplied, setJustApplied] = useState(false);




  // --- API LOGIC ---
 const fetchHotels = useCallback(async (searchTerm = '') => {
    try {
      setHotelsLoading(true);
      const hotelData = await apiService.getHotelList(searchTerm);
      setHotels(hotelData);
      
      if (hotelData.length > 0 && initialLoad && searchTerm === '') {
        const savedHotelId = state.searchState.hotelId;
        
        if (savedHotelId && state.searchState.hotel && state.searchState.hotel !== "ABC Hotel") {
          setInitialLoad(false);
          return;
        }
        
        if (savedHotelId) {
          const savedHotel = hotelData.find(h => h.id === savedHotelId);
          if (savedHotel) {
            dispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: { 
                hotel: savedHotel.name, 
                hotelId: savedHotel.id 
              }
            });
          } else {
            dispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: { 
                hotel: hotelData[0].name, 
                hotelId: hotelData[0].id 
              }
            });
          }
        } else {
          dispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: { 
              hotel: hotelData[0].name, 
              hotelId: hotelData[0].id 
            }
          });
        }
        setInitialLoad(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setHotelsLoading(false);
    }
  }, [dispatch, initialLoad, state.searchState.hotelId, state.searchState.hotel]); // Add hotel to dependencies

  useEffect(() => { fetchHotels(); }, [fetchHotels]);

  // Debounce Search
    useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(hotelSearch || hotelDropdownOpen || isDestinationOpen) fetchHotels(hotelSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [hotelSearch, hotelDropdownOpen, isDestinationOpen, fetchHotels]);

  // --- SCROLL LOCK FOR MOBILE MODALS ---
useEffect(() => {
    const lock = isMobileSearchOpen || isDestinationOpen || isGuestSheetOpen;
    document.body.style.overflow = lock ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isMobileSearchOpen, isDestinationOpen, isGuestSheetOpen]);

  // Close promo popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (promoRef.current && !promoRef.current.contains(e.target)) {
        setIsPromoOpen(false);
      }
    };
    if (isPromoOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPromoOpen]);

// Sync promoInput if promo_code gets cleared externally (e.g. hotel change)
  useEffect(() => {
    if (!state.searchState.promo_code) {
      setPromoInput('');
      setPromoApplied(false);
      setPromoError('');
      setWidgetPromoApplied(false);
    }
  }, [state.searchState.promo_code]);

  // FORCE SYNC: When context promo is cleared, reset all local states
useEffect(() => {
  if (!state.searchState.promo_code && !state.sidebarPromoApplied && promoApplied) {
    console.log('🔄 Force sync: Context promo cleared, resetting SearchBar');
    setPromoApplied(false);
    setPromoInput('');
    setPromoError('');
    setWidgetPromoApplied(false);
    setIsPromoOpen(false);
    setJustApplied(false);
  }
}, [state.searchState.promo_code, state.sidebarPromoApplied, promoApplied]);

// Clear promo when dates change - but only for manual promos and not right after application
// Clear promo when dates change - but only for manual promos and not right after application
// useEffect(() => {
//   // Skip if we have a promo code in state (it was just applied)
//   if (justApplied || state.searchState.promo_code) {
//     if (justApplied) {
//       console.log('⏸️ Skipping clear - just applied promo');
//     } else if (state.searchState.promo_code) {
//       console.log('⏸️ Skipping clear - promo exists in state');
//     }
//     return;
//   }
  
//   const isFromWidget = state.searchState.promo_from_widget;
//   const shouldClear = promoApplied && !isFromWidget && !widgetPromoApplied;
  
//   if (shouldClear) {
//     console.log('🗑️ Clearing promo due to date change');
//     dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
//     setPromoInput('');
//     setPromoApplied(false);
//     setPromoError('');
//   }
// }, [state.searchState.checkin, state.searchState.checkout, promoApplied, widgetPromoApplied, state.searchState.promo_from_widget, dispatch, justApplied, state.searchState.promo_code]);


// Add this ref to track previous dates
const prevDatesRef = useRef({ checkin: null, checkout: null });

// Function to handle date changes
const handleDatesChange = useCallback((newCheckin, newCheckout) => {
  // Check if dates actually changed
  const datesChanged = 
    prevDatesRef.current.checkin !== newCheckin ||
    prevDatesRef.current.checkout !== newCheckout;
  
  if (datesChanged) {
    console.log('📅 Dates changed, clearing promo code');
    
    // Clear all promo-related state
    dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
    setPromoInput('');
    setPromoApplied(false);
    setPromoError('');
    setWidgetPromoApplied(false);
    
    // Update the previous dates reference
    prevDatesRef.current = {
      checkin: newCheckin,
      checkout: newCheckout
    };
  }
}, [dispatch]);


// Persist hotel stackable info to localStorage
useEffect(() => {
  if (hotelStackableInfo.is_stackable !== undefined) {
    localStorage.setItem('hotel_stackable_info', JSON.stringify({
      ...hotelStackableInfo,
      timestamp: Date.now(),
      hotelId: state.searchState.hotelId
    }));
  }
}, [hotelStackableInfo, state.searchState.hotelId]);

// Restore hotel stackable info from localStorage on mount
useEffect(() => {
  const savedStackableInfo = localStorage.getItem('hotel_stackable_info');
  if (savedStackableInfo) {
    try {
      const stackableData = JSON.parse(savedStackableInfo);
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      
      // Check if data is not too old (1 hour) and matches current hotel
      if (now - stackableData.timestamp < oneHour && 
          stackableData.hotelId === state.searchState.hotelId) {
        console.log('🔄 Restoring hotel stackable info from localStorage:', stackableData);
        setHotelStackableInfo({
          is_stackable: stackableData.is_stackable,
          available_promo_codes: stackableData.available_promo_codes || []
        });
      } else {
        localStorage.removeItem('hotel_stackable_info');
      }
    } catch (error) {
      console.error('Error restoring stackable info:', error);
    }
  }
}, [state.searchState.hotelId]); // Re-run when hotel changes


useEffect(() => {
  // Skip if we just applied a promo
  if (justApplied) {
    return;
  }
  
  // Get current dates
  const currentCheckin = state.searchState.checkin;
  const currentCheckout = state.searchState.checkout;
  
  // Skip on initial load
  if (prevDatesRef.current.checkin === null && prevDatesRef.current.checkout === null) {
    prevDatesRef.current = {
      checkin: currentCheckin,
      checkout: currentCheckout
    };
    return;
  }
  
  // Handle date change
  handleDatesChange(currentCheckin, currentCheckout);
  
}, [state.searchState.checkin, state.searchState.checkout, justApplied, handleDatesChange]);


// Watch for promo applied state and update UI accordingly
useEffect(() => {
  console.log('🎨 UI Update Effect - promoApplied:', promoApplied, 'promo_code:', state.searchState.promo_code);
  
  // Don't update if we just applied
  if (justApplied) return;
  
  if (state.searchState.promo_code && !promoInput) {
    setPromoInput(state.searchState.promo_code);
  }
  
  if (state.searchState.promo_code && !promoApplied && state.searchState.promo_from_widget) {
    setPromoApplied(true);
    setWidgetPromoApplied(true);
  }
}, [state.searchState.promo_code, state.searchState.promo_from_widget, promoApplied, promoInput, justApplied]);


 useEffect(() => {
  const autoApplyWidgetPromo = async () => {
    const widgetPromoCode =  state.searchState.promo_code ? state.searchState.promo_code.toUpperCase() : null;
    const isFromWidget = state.searchState.promo_from_widget;
    
    console.log('🔍 Auto-apply widget promo check:', {
      widgetPromoCode,
      isFromWidget,
      widgetPromoApplied,
      promoApplied,
      hasHotel: !!state.searchState.hotelId,
      hasDates: !!(state.searchState.checkin && state.searchState.checkout)
    });
    
    if (!widgetPromoCode || !isFromWidget || widgetPromoApplied || promoApplied) {
      return;
    }
    
    if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
      console.log('⏳ Waiting for hotel and dates to load...');
      return;
    }
    
    console.log('🎯 Auto-applying widget promo code:', widgetPromoCode);
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const formatDate = (d) => {
        const date = new Date(d);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      
      const searchParams = {
        startDate: formatDate(state.searchState.checkin),
        endDate: formatDate(state.searchState.checkout),
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
        promo_code: widgetPromoCode,
      };
      
      const hotelData = await apiService.getHotelData(
        state.searchState.hotelId,
        searchParams
      );
       setHotelStackableInfo({
        is_stackable: hotelData?.is_stackable || false,
        available_promo_codes: hotelData?.available_promo_codes || []
      });
      
      const promoCodeInfo = hotelData?.promo_code_info;
      
      console.log('📦 API Response - promo_code_info:', promoCodeInfo);
      
      if (!promoCodeInfo || promoCodeInfo.is_valid === false) {
        const errorMessage = promoCodeInfo?.message || 'Invalid promo code';
        console.warn('❌ Widget promo code invalid:', widgetPromoCode, errorMessage);
        setPromoError(errorMessage);
        
        dispatch({
          type: 'UPDATE_SEARCH_STATE',
          payload: { promo_code: '', promo_from_widget: false }
        });
        setWidgetPromoApplied(true);
        setPromoApplied(false);
        setPromoInput('');
        setPromoLoading(false);
        return;
      }
      
     // Inside your autoApplyWidgetPromo function, when the promo is successfully applied:
if (promoCodeInfo.applied === true) {
  console.log('✅ Widget promo valid and applied!');
  
  // Set justApplied flag to prevent immediate clearing
  setJustApplied(true);
  
 // Calculate total discount from rate plans
        let totalDiscount = 0;
        let discountDetails = [];

       hotelData?.categories?.forEach(category => {
  category?.rate_plans?.forEach(plan => {
    const hasPromoOffer = plan?.offer?.is_promo_code === true && 
                          plan?.offer?.promo_code === widgetPromoCode;
    const hasDiscount = (plan.originalPrice || 0) > (plan.price || 0);
    
    if (hasPromoOffer && hasDiscount) {
      const saving = (plan.originalPrice || 0) - (plan.price || 0);
      if (saving > 0) {
        totalDiscount += saving;
        discountDetails.push({
          category: category.name,
          plan: plan.name,
          saving: saving
        });
        console.log(`💰 Discount for ${plan.name}: ${saving}`);
      }
    }
  });
});

// If no discount found, check for price reductions
if (totalDiscount === 0) {
  console.log('⚠️ No discount from promo flag, checking price reductions...');
  hotelData?.categories?.forEach(category => {
    category?.rate_plans?.forEach(plan => {
      if ((plan.originalPrice || 0) > (plan.price || 0)) {
        const saving = (plan.originalPrice || 0) - (plan.price || 0);
        totalDiscount += saving;
        console.log(`💰 Price reduction discount for ${plan.name}: ${saving}`);
      }
    });
  });
}

console.log('✅ Total discount amount:', totalDiscount, discountDetails);
        
        // CRITICAL: Update all state to show promo as applied
        setPromoApplied(true);
        setPromoInput(widgetPromoCode);
        setPromoError('');
        setWidgetPromoApplied(true);
        
        // Close the promo popup if open
        setIsPromoOpen(false);
        
        // Apply the promo discount to context
        dispatch({
          type: 'APPLY_WIDGET_PROMO',
          payload: {
            discountAmount: totalDiscount,
            promoCode: widgetPromoCode,
          },
        });
        
        // Update search state with API response
        dispatch({
          type: 'PERFORM_SEARCH',
          payload: {
            hotelId: state.searchState.hotelId,
            checkin: state.searchState.checkin,
            checkout: state.searchState.checkout,
            rooms: state.rooms.length,
            guests: state.searchState.guestCount,
            promo_code: widgetPromoCode,
            apiResponse: hotelData
          }
        });
        
 
  // Reset justApplied flag after a short delay
  setTimeout(() => {
     console.log('🔄 Triggering search results update');
    setJustApplied(false);
  }, 500);
} else {
        setPromoError(promoCodeInfo?.message || 'Promo code could not be applied');
        setPromoApplied(false);
      }
      
    } catch (err) {
      console.error('❌ Widget promo validation error:', err);
      setPromoError('Could not validate promo code. Please try again.');
    } finally {
      setPromoLoading(false);
    }
  };
  
  if (state.searchState.promo_code && state.searchState.promo_from_widget && !widgetPromoApplied) {
    const timer = setTimeout(autoApplyWidgetPromo, 1000);
    return () => clearTimeout(timer);
  }
}, [state.searchState.promo_code, state.searchState.promo_from_widget, state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, widgetPromoApplied, promoApplied, dispatch, state.rooms, state.searchState.guestCount]);

useEffect(() => {
  const autoApplyToCart = async () => {
    // Check if we have a widget promo waiting and cart has items
    if (widgetPromoApplied && state.cart.length > 0 && !state.hasDiscount) {
      console.log('🔄 Auto-applying widget promo to cart items');
      
      // Trigger the promo application to cart
      const promoCode = state.searchState.promo_code;
      if (promoCode) {
        // Create a synthetic event to apply promo to cart
        const promoData = prepareCartDataForPromo();
        if (promoData) {
          // Dispatch action to apply promo
          dispatch({
            type: 'APPLY_WIDGET_PROMO_TO_CART',
            payload: {
              promoCode: promoCode,
              // Include any discount info from the original widget promo
            }
          });
        }
      }
    }
  };
  
  autoApplyToCart();
}, [state.cart.length, widgetPromoApplied, state.hasDiscount, state.searchState.promo_code]);

// SYNC: Listen for sidebar promo applications
useEffect(() => {
  // If sidebar applied a promo, sync to SearchBar
  if (state.sidebarPromoApplied && state.sidebarPromoCode) {
    console.log('🔄 Syncing sidebar promo to SearchBar:', state.sidebarPromoCode);
    
    // Update SearchBar state to match sidebar
    setPromoInput(state.sidebarPromoCode);
    setPromoApplied(true);
    setWidgetPromoApplied(false); // Not from widget
    setPromoError('');
    
    // Update context search state
    dispatch({
      type: 'UPDATE_SEARCH_STATE',
      payload: {
        promo_code: state.sidebarPromoCode,
        promo_from_widget: false
      }
    });
    
    // Clear sidebar promo flag to prevent re-triggering
    dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
  }
}, [state.sidebarPromoApplied, state.sidebarPromoCode, dispatch]);

// SYNC: When SearchBar promo is cleared, notify sidebar
useEffect(() => {
  // If SearchBar promo is cleared and sidebar thinks it's applied
  if (!promoApplied && !state.searchState.promo_code && state.sidebarPromoApplied) {
    console.log('🔄 SearchBar promo cleared, syncing to sidebar');
    dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
  }
}, [promoApplied, state.searchState.promo_code, state.sidebarPromoApplied, dispatch]);

// Add a new function to prepare cart data for promo API
// Add this function to prepare cart data for promo API - UPDATED to match API expectations
const prepareCartDataForPromo = useCallback((responseData = null) => {
  if (!state.cart || state.cart.length === 0) return null;
  
  const formatDate = (d) => {
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  
  const checkIn = new Date(state.searchState.checkin);
  const checkOut = new Date(state.searchState.checkout);
  const totalNights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  
  // Calculate total guests
  const totalGuests = state.cart.reduce((total, item) => {
    return total + (item.adultsCount || 2) + (item.childrenCount || 0);
  }, 0);
  
  const roomDetails = state.cart.map((item, index) => {
    // Get adults count from multiple possible sources
    let adultsCount = 2;
    let childrenCount = 0;
    let childAges = [];
    
    // Check guestInfo first
    if (item.guestInfo) {
      adultsCount = item.guestInfo.adults || item.adultsCount || 2;
      childrenCount = item.guestInfo.children || item.childrenCount || 0;
      childAges = item.guestInfo.childAges || [];
    } 
    // Check originalGuestConfig
    else if (item.originalGuestConfig) {
      adultsCount = item.originalGuestConfig.adults || 2;
      childrenCount = item.originalGuestConfig.children || 0;
      childAges = item.originalGuestConfig.childAges || [];
    }
    // Check searchState room_guest
    else if (state.searchState?.room_guest && item.roomNumber) {
      const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
      if (roomGuest) {
        adultsCount = roomGuest.no_of_adults || 2;
        childrenCount = roomGuest.no_of_children || 0;
        childAges = roomGuest.child_ages || [];
      }
    }
    // Fallback to item properties
    else {
      adultsCount = item.adultsCount || 2;
      childrenCount = item.childrenCount || 0;
      childAges = item.childAges || [];
    }
    
    // Calculate price per night
    const pricePerNight = item.roomPrice || (item.totalForStay / totalNights);
    const originalPricePerNight = item.originalPrice || pricePerNight;
    
    // Format child ages as array of objects with age property
    const formattedChildAges = Array.isArray(childAges) && childAges.length > 0
      ? childAges.map(age => {
          if (typeof age === 'object' && age !== null) {
            return { age: parseInt(age.age, 10) };
          }
          return { age: parseInt(age, 10) };
        })
      : [];
    
    let discountId = null;
    
    // Get discount_id from room detail in API response if available
    if (responseData?.room_details && responseData.room_details[index]) {
      discountId = responseData.room_details[index].discount_id || 
                   responseData.room_details[index].offer_id ||
                   responseData.room_details[index].offer?.id;
    }
    
    // Also check from the room's own data if available
    if (!discountId && item.discountId) {
      discountId = item.discountId;
    }
    
    return {
      room_number: index + 1,
      category_id: item.categoryId || item.roomId,
      category_name: item.roomName,
      rate_plan_id: item.ratePlanId,
      rate_plan_name: item.rateName,
      adults_count: adultsCount,
      children_count: childrenCount,
      child_ages: formattedChildAges,
      room_price: parseFloat(pricePerNight),
      original_price: parseFloat(originalPricePerNight),
      quantity: item.quantity || 1,
      offer_applied: item.offerName || '',
      ...(discountId && { discount_id: discountId })
    };
  });
  
  const subtotal = state.subtotal || state.cart.reduce((sum, item) => sum + (item.totalForStay || 0), 0);
  
  const payload = {
    promo_code: promoInput ? promoInput.toUpperCase() : null,  // FIXED: Use promoInput
    booking_details: {
      hotel_id: parseInt(state.searchState.hotelId),
      hotel_name: state.searchState.hotel,
      check_in: formatDate(state.searchState.checkin),
      check_out: formatDate(state.searchState.checkout),
      total_nights: totalNights,
      total_guests: totalGuests,
      total_rooms: state.cart.reduce((total, item) => total + (item.quantity || 1), 0)
    },
    room_details: roomDetails,
    subtotal: parseFloat(subtotal)
  };
  
  // If we have a discount_id at the booking level, include it
  if (currentPromoDiscountId) {
    payload.discount_id = currentPromoDiscountId;
  }
  
  return payload;
}, [state.cart, state.searchState, state.subtotal, promoInput, currentPromoDiscountId]); // FIXED: Add promoInput to dependencies
const getOfferIdFromRatePlan = (ratePlan) => {
  if (ratePlan?.offer?.id) {
    return ratePlan.offer.id;
  }
  return null;
};

// Helper to get all offer IDs from selected cart items
const getSelectedRoomOfferIds = useCallback(() => {
  const offerIds = [];
  
  if (state.cart && state.cart.length > 0) {
    state.cart.forEach(item => {
      if (item.ratePlan?.offer?.id) {
        offerIds.push({
          roomNumber: item.roomNumber,
          categoryId: item.categoryId,
          ratePlanId: item.ratePlanId,
          offerId: item.ratePlan.offer.id,
          offerName: item.ratePlan.offer.discount_name
        });
      }
    });
  }
  
  
  return offerIds;
}, [state.cart]);

// Check if promo code is stackable based on API response
// Check if promo code is stackable based on API response
const isPromoStackable = useCallback((promoCode) => {
  if (!promoCode) return false;
  
  console.log(`🔍 Checking if ${promoCode} is stackable...`);
  
  // First check root level is_stackable from searchResults
  if (state.searchResults?.is_stackable === true) {
    console.log(`✅ ${promoCode} is stackable (global flag from searchResults)`);
    return true;
  }
  
  // Check hotelStackableInfo
  if (hotelStackableInfo?.is_stackable === true) {
    console.log(`✅ ${promoCode} is stackable (hotel flag from hotelStackableInfo)`);
    return true;
  }
  
  // Check available_promo_codes for the specific promo code
  const availablePromoCodes = state.searchResults?.available_promo_codes || 
                              hotelStackableInfo?.available_promo_codes;
  
  if (availablePromoCodes && availablePromoCodes.length > 0) {
    const promoInfo = availablePromoCodes.find(p => p.code === promoCode);
    if (promoInfo) {
      const isStackable = promoInfo.is_stackable === 1 || promoInfo.is_stackable === true;
      console.log(`✅ ${promoCode} found in available promos, is_stackable: ${isStackable}`);
      return isStackable;
    }
  }
  
  // CRITICAL FIX: Check if there's a stackable offer already applied on any cart item
  // This means the promo can be stacked with existing offers
  const hasStackableOfferApplied = state.cart.some(item => {
    if (item.ratePlan?.offer?.is_stackable === true) return true;
    if (item.isStackableApplied === true) return true;
    return false;
  });
  
  if (hasStackableOfferApplied) {
    console.log(`✅ ${promoCode} can be applied because stackable offer exists on cart`);
    return true;
  }
  
  console.log(`❌ ${promoCode} is NOT stackable`);
  return false;
}, [state.searchResults, hotelStackableInfo, state.cart]);



// Add this helper function to check if any other offers are applied
const hasAnyOtherOfferApplied = useCallback(() => {
  // Check if there are cart items with non-promo offers
  if (state.cart && state.cart.length > 0) {
    return state.cart.some(item => {
      // Check for free night offers
      if (item.isFreeNightOffer) return true;
      if (item.freeNights > 0) return true;
      
      // Check for rate plan offers that are NOT promo codes
      if (item.ratePlan?.offer) {
        const offer = item.ratePlan.offer;
        // If it's a promo code offer, it's allowed
        if (offer.is_promo_code === true) return false;
        // Any other offer type means promo cannot be applied
        return true;
      }
      
      // Check for discount info
      if (item.discountInfo && !item.discountInfo.is_promo_code) {
        return true;
      }
      
      // Check for offer name
      if (item.offerName && !item.offerName.includes('Promo')) {
        return true;
      }
      
      return false;
    });
  }
  
  return false;
}, [state.cart]);

// Add this to check if the current search results have offers
const hasOffersInSearchResults = useCallback(() => {
  // Check if any rate plan has an offer that's NOT a promo code
  if (state.searchResults?.categories) {
    return state.searchResults.categories.some(category => {
      return category.rate_plans?.some(plan => {
        if (plan.offer && plan.offer.is_promo_code !== true) {
          // This is a non-promo offer (Base Deal, Last Minute, etc.)
          console.log(`⚠️ Found non-promo offer: ${plan.offer.discount_name} on ${plan.name}`);
          return true;
        }
        return false;
      });
    });
  }
  return false;
}, [state.searchResults]);


const hasOtherOffersApplied = useCallback(() => {
  // Check if there are any rooms selected
  if (!state.cart || state.cart.length === 0) {
    console.log('✅ No cart items, allowing promo');
    return false;
  }
  
    // CRITICAL FIX: Check if a stackable promo is currently applied FIRST
  const hasStackablePromoApplied = state.searchState.promo_code && 
    isPromoStackable(state.searchState.promo_code);
  
  if (hasStackablePromoApplied) {
    console.log('✅ Stackable promo already applied, allowing');
    return false;
  }
  
  // CRITICAL FIX: Check if the current promo being entered is stackable SECOND
  const currentPromoCode = promoInput;
  const isCurrentPromoStackable = currentPromoCode && isPromoStackable(currentPromoCode);
  
  // If the user is trying to apply a stackable promo, allow it regardless of other offers
  if (isCurrentPromoStackable) {
    console.log('✅ Current promo is stackable, allowing application');
    return false;
  }
  
  // CRITICAL FIX: Check global stackable flag THIRD
  let globalStackable = false;
  
  // Check searchResults
  if (state.searchResults?.is_stackable === true) {
    globalStackable = true;
    console.log('✅ Global stackable from searchResults');
  }
  
  // Check hotelStackableInfo
  if (hotelStackableInfo?.is_stackable === true) {
    globalStackable = true;
    console.log('✅ Global stackable from hotelStackableInfo');
  }
  
  // Check localStorage for persisted stackable info
  if (!globalStackable) {
    const savedInfo = localStorage.getItem('hotel_stackable_info');
    if (savedInfo) {
      try {
        const info = JSON.parse(savedInfo);
        if (info.hotelId === state.searchState.hotelId && 
            info.timestamp > Date.now() - 3600000 &&
            info.is_stackable === true) {
          globalStackable = true;
          console.log('✅ Global stackable from localStorage');
        }
      } catch (e) {}
    }
  }
  
  // If stackable is enabled globally, allow promo application
  if (globalStackable) {
    console.log('✅ Global stackable enabled, allowing promo application');
    return false;
  }
  
  // ONLY check for non-stackable offers if NONE of the above conditions are met
  const hasNonStackableOffer = state.cart.some(item => {
    // Check for free night offers
    if (item.isFreeNightOffer) {
      console.log('⚠️ Found free night offer that is non-stackable');
      return true;
    }
    if (item.freeNights > 0) {
      console.log('⚠️ Found free nights that are non-stackable');
      return true;
    }
    
    // Check for rate plan offers
    if (item.ratePlan?.offer) {
      const offer = item.ratePlan.offer;
      // If it's a promo code offer, it's allowed
      if (offer.is_promo_code === true) return false;
      // Check if the offer itself is stackable
      if (offer.is_stackable === true) {
        console.log('✅ Found stackable offer, allowing promo');
        return false;
      }
      // Any other offer type that's not stackable
      console.log(`⚠️ Found non-stackable offer: ${offer.discount_name || offer.name || 'unknown'}`);
      return true;
    }
    
    // Check for discount info that's not stackable
    if (item.discountInfo && !item.discountInfo.is_promo_code) {
      if (item.discountInfo.is_stackable === true) return false;
      console.log('⚠️ Found non-stackable discount info');
      return true;
    }
    
    // Check for offer name that's not a promo
    if (item.offerName && !item.offerName.toLowerCase().includes('promo')) {
      console.log(`⚠️ Found non-promo offer name: ${item.offerName}`);
      return true;
    }
    
    return false;
  });
  
  console.log('hasNonStackableOffer result:', hasNonStackableOffer);
  return hasNonStackableOffer;
}, [state.cart, state.searchState.promo_code, state.searchState.hotelId, state.searchResults?.is_stackable, hotelStackableInfo, isPromoStackable, promoInput]);


// Add this effect to re-validate promo when cart changes (for non-stackable promos)
useEffect(() => {
  // Only run if we have an applied promo and cart has items
  if (promoApplied && state.cart.length > 0 && !state.hasDiscount) {
    // Check if the applied promo is non-stackable and cart items have offers
    const hasOffers = hasOtherOffersApplied();
    const isStackable = isPromoStackable(state.searchState.promo_code);
    
    console.log('🔄 Re-validating promo after cart change:', {
      promoApplied,
      hasCartItems: state.cart.length > 0,
      hasOffers,
      isStackable,
      promoCode: state.searchState.promo_code
    });
    
    // If non-stackable promo and cart has offers, show promo as unavailable
    if (hasOffers && !isStackable) {
      console.log('⚠️ Non-stackable promo cannot be applied because cart has offers');
      setPromoError('Promo code cannot be combined with existing offers on selected rooms.');
      setPromoApplied(false);
      setWidgetPromoApplied(false);
      
      // Clear the promo from context
      dispatch({ 
        type: 'UPDATE_SEARCH_STATE', 
        payload: { promo_code: '', promo_from_widget: false } 
      });
      
      // Clear any pending discount
      dispatch({ type: 'CLEAR_DISCOUNT' });
      
      // Show a temporary notification
      setPromoError('Promo code removed - cannot combine with selected room offers.');
      
      // Clear error after 5 seconds
      setTimeout(() => {
        if (promoError === 'Promo code removed - cannot combine with selected room offers.') {
          setPromoError('');
        }
      }, 5000);
    }
  }
}, [state.cart.length, promoApplied, state.hasDiscount, state.searchState.promo_code, hasOtherOffersApplied, isPromoStackable, dispatch]);


// Also check if the current search results have other offers (for cases without cart items)
const hasOtherOffersInResults = useCallback(() => {
  if (state.searchResults?.categories) {
    return state.searchResults.categories.some(category => {
      return category.rate_plans?.some(plan => {
        if (plan.offer && plan.offer.is_promo_code !== true && plan.offer) {
          return true;
        }
        return false;
      });
    });
  }
  return false;
}, [state.searchResults]);

const canApplyPromo = useCallback((promoCode) => {
  const hasCartItems = state.cart && state.cart.length > 0;
  
  if (!hasCartItems) {
    return true; // Can always apply promo before selecting rooms
  }
  
  // Check global stackable flag from search results
  const globalStackable = state.searchResults?.is_stackable === true;
  const stackable = isPromoStackable(promoCode);
  
  // If stackable is enabled globally OR the specific promo is stackable
  if (globalStackable || stackable) {
    return true; // Can apply even with existing offers
  }
  
  // If not stackable, check if there are any other offers
  const hasOtherOffers = hasOtherOffersApplied();
  
  if (hasOtherOffers) {
    return false; // Cannot apply non-stackable promo with existing offers
  }
  
  return true;
}, [state.cart, state.searchResults?.is_stackable, isPromoStackable, hasOtherOffersApplied]);

// Get offer type description for tooltip
const getOfferDescription = useCallback(() => {
  if (state.cart && state.cart.length > 0) {
    for (const item of state.cart) {
      if (item.isFreeNightOffer && item.freeNights > 0) {
        return `Free Night Offer (${item.freeNights} night${item.freeNights > 1 ? 's' : ''} free)`;
      }
      if (item.ratePlan?.offer?.discount_name) {
        return item.ratePlan.offer.discount_name;
      }
      if (item.offerName) {
        return item.offerName;
      }
    }
  }
  
  if (state.searchResults?.categories) {
    for (const category of state.searchResults.categories) {
      for (const plan of category.rate_plans || []) {
        if (plan.offer?.discount_name) {
          return plan.offer.discount_name;
        }
      }
    }
  }
  
  return 'other offer';
}, [state.cart, state.searchResults]);

// Check if promo should be disabled
const isPromoDisabled = hasOtherOffersApplied() || hasOtherOffersInResults();
const offerDescription = getOfferDescription();


// Updated handleApplyPromo function
const handleApplyPromo = async () => {
   // Convert promo input to uppercase
  const trimmedPromoCode = promoInput.trim().toUpperCase();
  
  if (!trimmedPromoCode) {
    setPromoError('Please enter a promo code.');
    return;
  }


  if (!state.searchState.hotelId) {
    setPromoError('Please select a hotel first.');
    return;
  }
  
  if (!state.searchState.checkin || !state.searchState.checkout) {
    setPromoError('Please select check-in and check-out dates first.');
    return;
  }

   // Check if promo can be applied
  if (!canApplyPromo(trimmedPromoCode)) {
    setPromoError('Promo code cannot be combined with existing offers on selected rooms.');
    setPromoApplied(false);
    return;
  }

  setPromoLoading(true);
  setPromoError('');

  try {
    const formatDate = (d) => {
      const date = new Date(d);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Check if we have cart items (rooms selected)
    const hasCartItems = state.cart && state.cart.length > 0;
    
    // Get offer IDs from selected rooms if any
    const selectedOfferIds = hasCartItems ? getSelectedRoomOfferIds() : [];
    const isStackable = isPromoStackable(promoInput.trim());
    
    if (hasCartItems && !isStackable && selectedOfferIds.length > 0) {
      // This should have been caught by canApplyPromo, but double-check
      setPromoError('Promo code cannot be combined with existing offers on selected rooms.');
      setPromoApplied(false);
      setPromoLoading(false);
      return;
    }
    
   
  if (hasCartItems) {
   // ==========================================================
  // SCENARIO 1: CART HAS ITEMS - Use promo-discount API with offer IDs
  // ==========================================================
  console.log('📦 Cart has items, using promo-discount API');
  
  const promoData = prepareCartDataForPromo();
  promoData.promo_code = trimmedPromoCode;
  if (!promoData) {
    setPromoError('Unable to prepare cart data');
    setPromoLoading(false);
    return;
  }
  
  // Add offer IDs to the promo data if available
  if (selectedOfferIds.length > 0) {
    promoData.offer_details = selectedOfferIds.map(offer => ({
      room_number: offer.roomNumber,
      offer_id: offer.offerId,
      category_id: offer.categoryId,
      rate_plan_id: offer.ratePlanId
    }));
  }
  
  // CRITICAL: Store the original cart state BEFORE applying promo
  const originalCartState = {
    cart: JSON.parse(JSON.stringify(state.cart)),
    subtotal: state.subtotal,
    taxes: state.taxes,
    total: state.total,
    finalTotal: state.finalTotal,
    hasDiscount: state.hasDiscount,
    discountAmount: state.discountAmount,
    promoCode: state.promoCode
  };
  
  localStorage.setItem('original_cart_state', JSON.stringify(originalCartState));
  
  promoData.is_stackable = isStackable;
  
  console.log('📦 Sending promo data to API:', JSON.stringify(promoData, null, 2));
  
  const promoResponse = await apiService.applyPromoCodeToCart(promoData);
  
  console.log('📦 Cart promo validation response:', promoResponse);
  
  if (!promoResponse.success) {
    setPromoError(promoResponse.message || 'Invalid promo code');
    setPromoApplied(false);
    setPromoLoading(false);
    return;
  }
  
  // Extract data from API response
  const responseData = promoResponse.data;
  const discountAmount = responseData?.savings_summary?.total_savings || 0;
  const discountedSubtotal = responseData?.discounted_charges?.subtotal || 0;
  const discountedTaxes = responseData?.discounted_charges?.taxes?.total_tax || 0;
  const discountedTotal = responseData?.discounted_charges?.total || 0;
  const roomDetailsFromResponse = responseData?.room_details || [];
  
  // ⭐ CRITICAL: Extract discount_id from API response
  let discountId = null;
  if (responseData?.discount_id) {
    discountId = responseData.discount_id;
  } else if (responseData?.offer_id) {
    discountId = responseData.offer_id;
  } else if (responseData?.id) {
    discountId = responseData.id;
  } else if (roomDetailsFromResponse.length > 0 && roomDetailsFromResponse[0]?.discount_id) {
    discountId = roomDetailsFromResponse[0].discount_id;
  } else if (roomDetailsFromResponse.length > 0 && roomDetailsFromResponse[0]?.offer_id) {
    discountId = roomDetailsFromResponse[0].offer_id;
  } else if (roomDetailsFromResponse.length > 0 && roomDetailsFromResponse[0]?.offer?.id) {
    discountId = roomDetailsFromResponse[0].offer.id;
  }
  
  // Store the discount_id for future use
  if (discountId) {
    setCurrentPromoDiscountId(discountId);
    console.log('💰 Stored discount_id from API:', discountId);
  } else {
    console.warn('⚠️ No discount_id found in API response:', responseData);
  }
  
  console.log('📦 API Response taxes:', {
    discountedTaxes,
    total_tax: responseData?.discounted_charges?.taxes?.total_tax,
    tax_breakdown: responseData?.discounted_charges?.taxes
  });
  
  // Double-check: If discountAmount is 0, the promo didn't actually apply
  if (discountAmount === 0) {
    setPromoError('Promo code could not be applied to this booking.');
    setPromoApplied(false);
    setPromoLoading(false);
    return;
  }
  
  // Calculate tax per room from the total discounted taxes
  const totalRooms = state.cart.length;
  const taxPerRoom = discountedTaxes / totalRooms;
  
  console.log(`Total tax: ${discountedTaxes}, Rooms: ${totalRooms}, Tax per room: ${taxPerRoom}`);
  
  // Update cart items with discounted prices AND discount_id
  const updatedCart = state.cart.map((item, index) => {
    const roomDetail = roomDetailsFromResponse.find(r => r.room_number === index + 1);
    
    if (roomDetail) {
      const discountedPricePerNight = roomDetail.discounted_price_per_night;
      const nights = calculateNights();
   
      const hasExistingOffer = item.ratePlan?.offer && !item.ratePlan.offer.is_promo_code;
      
      const globalStackable = hotelStackableInfo?.is_stackable === true || 
                             state.searchResults?.is_stackable === true;
      
      let promoStackable = false;
      const availablePromoCodes = hotelStackableInfo?.available_promo_codes || 
                                 state.searchResults?.available_promo_codes;
      if (availablePromoCodes && promoInput.trim()) {
        const promoInfo = availablePromoCodes.find(p => p.code === promoInput.trim());
        promoStackable = promoInfo?.is_stackable === 1;
      }
      
      const isStackableApplied = hasExistingOffer && (globalStackable || promoStackable);
      const originalRoomPrice = item.roomPrice;
      const originalTaxData = item.taxData;


      
// With this new logic:
let roomDiscountId = null;

if (hasExistingOffer && isStackableApplied) {
  // When stacking: Use the EXISTING OFFER'S ID from the cart item
  roomDiscountId = item.ratePlan?.offer?.id || 
                   item.discountId || 
                   item.ratePlan?.offer?.discount_id;
  console.log(`💰 Room ${index + 1} - Using EXISTING offer ID for stacking: ${roomDiscountId}`);
} else if (roomDetail.discount_id || roomDetail.offer_id || roomDetail.offer?.id) {
  // When promo is the only offer: Use the promo's discount ID from API response
  roomDiscountId = roomDetail.discount_id || 
                   roomDetail.offer_id || 
                   roomDetail.offer?.id || 
                   discountId;
  console.log(`💰 Room ${index + 1} - Using PROMO discount ID: ${roomDiscountId}`);
} else if (discountId) {
  roomDiscountId = discountId;
  console.log(`💰 Room ${index + 1} - Using fallback discount ID: ${roomDiscountId}`);
}

      
      return {
        ...item,
        originalRoomPrice: originalRoomPrice,
        originalTaxData: originalTaxData,
        roomName: item.roomName,
        rateName: item.rateName,
        categoryId: item.categoryId,
        roomId: item.roomId,
        ratePlanId: item.ratePlanId,
        roomNumber: item.roomNumber,
        quantity: item.quantity,
        nights: item.nights,
        discountedPricePerNight: discountedPricePerNight,
        roomPrice: discountedPricePerNight,
        originalPrice: roomDetail.original_price_per_night || item.originalPrice,
        discountAmount: roomDetail.discount_amount || 0,
        // ⭐ CRITICAL: Store discount_id on cart item
        discountId: roomDiscountId,
        isStackableApplied: isStackableApplied,
        hasExistingOffer: hasExistingOffer,
        offerOverriddenByPromo: hasExistingOffer && !isStackableApplied,
        taxData: {
          total_tax_amount: taxPerRoom,
          total_cgst_amount_after_tax: taxPerRoom / 2,
          total_sgst_amount_after_tax: taxPerRoom / 2,
          total_cess_amount: 0,
        },
        ...(isStackableApplied && item.ratePlan?.offer && {
          stackedOffers: [
            ...(item.stackedOffers || []),
            {
              ...item.ratePlan.offer,
              is_stackable_applied: true,
              discount_id: roomDiscountId
            }
          ]
        }),
        ratePlan: {
          ...item.ratePlan,
          offer: item.ratePlan?.offer ? {
            ...item.ratePlan.offer,
            overridden_by_promo: !isStackableApplied,
            is_overridden: !isStackableApplied,
            is_stackable_applied: isStackableApplied,
            discount_id: roomDiscountId
          } : null
        },
        offerName: !isStackableApplied && hasExistingOffer 
          ? `${item.offerName || item.ratePlan?.offer?.discount_name || ''} (overridden by promo)`
          : item.offerName || item.ratePlan?.offer?.discount_name,
        isFreeNightOffer: false,
        freeNights: 0,
        hasPromoApplied: true,
        promoCodeApplied: promoInput.trim()
      };
    }
    return item;
  });
    
    // Set justApplied flag to prevent immediate clearing
    setJustApplied(true);
    
    // Update state
    setPromoApplied(true);
    setPromoError('');
       setPromoInput(trimmedPromoCode);

    // ADD THIS LINE - Notify sidebar that promo is applied
dispatch({ type: 'SET_SIDEBAR_PROMO', payload: { promoCode: promoInput.trim(), applied: true } });
    // Close the promo popup
    setIsPromoOpen(false);
    
    // Update search state
    dispatch({
      type: 'UPDATE_SEARCH_STATE',
      payload: { 
        promo_code: promoInput.trim(),
        promo_from_widget: false
      },
    });
    
    // Apply the promo discount to cart with updated cart items and tax data
    dispatch({
      type: 'APPLY_MANUAL_PROMO',
      payload: {
        discountAmount: discountAmount,
        promoCode: promoInput.trim(),
        discountedSubtotal: discountedSubtotal,
        discountedTaxes: discountedTaxes,
        discountedTotal: discountedTotal,
        finalTotal: discountedTotal,
        isStackable: isStackable,
        promoCodeInfo: {
          ...responseData?.promo_code_info,
          id: responseData?.room_details?.[0]?.offer?.id,
          room_details: responseData?.room_details,
          discounted_charges: responseData?.discounted_charges,
          savings_summary: responseData?.savings_summary,
          is_stackable: isStackable
        },
        updatedCart: updatedCart,
        fromWidget: false
      }
    });
    
    // CRITICAL: Save to localStorage for persistence
    const persistentState = {
      cart: updatedCart,
      hasDiscount: true,
      promoCode: promoInput.trim(),
      discountAmount: discountAmount,
      discountedSubtotal: discountedSubtotal,
      discountedTaxes: discountedTaxes,
      discountedTotal: discountedTotal,
      timestamp: Date.now()
    };
    
    localStorage.setItem('booking_promo_state', JSON.stringify(persistentState));
    
    console.log('✅ Cart promo applied successfully, discount amount:', discountAmount, 'Stackable:', isStackable);
    console.log('✅ Tax data saved:', updatedCart.map(r => r.taxData));
    
    // Reset justApplied flag after a short delay
    setTimeout(() => {
      setJustApplied(false);
    }, 500);
  } else {
  // ==========================================================
  // SCENARIO 2: NO CART ITEMS - Use existing getHotelData API
  // ==========================================================
  console.log('📦 No cart items, using existing getHotelData API');
  
  const searchParams = {
    startDate: formatDate(state.searchState.checkin),
    endDate: formatDate(state.searchState.checkout),
    rooms: state.rooms.length,
    guests: state.searchState.guestCount,
    roomsData: state.rooms,
    promo_code: trimmedPromoCode,
  };
  
  const hotelData = await apiService.getHotelData(
    state.searchState.hotelId,
    searchParams
  );
  
  setHotelStackableInfo({
    is_stackable: hotelData?.is_stackable || false,
    available_promo_codes: hotelData?.available_promo_codes || []
  });
  
  const promoCodeInfo = hotelData?.promo_code_info;
  
  console.log('📦 Manual promo validation response:', promoCodeInfo);
  
  if (!promoCodeInfo || promoCodeInfo.is_valid === false) {
    setPromoError(promoCodeInfo?.message || 'Invalid promo code');
    setPromoApplied(false);
    setPromoLoading(false);
    return;
  }
  
  if (promoCodeInfo.applied === true) {
  // IMPROVED: Calculate total discount from rate plans with better logging
  let totalDiscount = 0;
  let discountDetails = [];
  
  hotelData?.categories?.forEach(category => {
    category?.rate_plans?.forEach(plan => {
      // Check if this rate plan has the promo code applied
      const hasPromoOffer = plan?.offer?.is_promo_code === true && 
                            plan?.offer?.promo_code === trimmedPromoCode;
      
      // Also check if the rate plan's price is less than original price
      const hasDiscount = (plan.originalPrice || 0) > (plan.price || 0);
      
      if (hasPromoOffer && hasDiscount) {
        const saving = (plan.originalPrice || 0) - (plan.price || 0);
        if (saving > 0) {
          totalDiscount += saving;
          discountDetails.push({
            category: category.name,
            plan: plan.name,
            originalPrice: plan.originalPrice,
            discountedPrice: plan.price,
            saving: saving
          });
          console.log(`💰 Discount for ${category.name} - ${plan.name}: ${saving}`);
        }
      }
    });
  });
  
  // If no discount found from promo offer flag, check for any price reduction
  if (totalDiscount === 0) {
    console.log('⚠️ No discount found from promo flag, checking for price reductions...');
    hotelData?.categories?.forEach(category => {
      category?.rate_plans?.forEach(plan => {
        if ((plan.originalPrice || 0) > (plan.price || 0)) {
          const saving = (plan.originalPrice || 0) - (plan.price || 0);
          totalDiscount += saving;
          discountDetails.push({
            category: category.name,
            plan: plan.name,
            originalPrice: plan.originalPrice,
            discountedPrice: plan.price,
            saving: saving,
            source: 'price_reduction'
          });
          console.log(`💰 Price reduction for ${category.name} - ${plan.name}: ${saving}`);
        }
      });
    });
  }
  
  console.log('✅ Total discount amount:', totalDiscount, 'Details:', discountDetails);
  
  if (totalDiscount === 0) {
    // Instead of error, check if the API returned discounted totals directly
    if (hotelData?.discounted_subtotal && hotelData?.discounted_total) {
      console.log('📦 Using API discounted totals instead of calculated discount');
      totalDiscount = (hotelData.original_subtotal || hotelData.subtotal || 0) - hotelData.discounted_subtotal;
      
      if (totalDiscount > 0) {
        console.log('✅ Using API discount amount:', totalDiscount);
      } else {
        setPromoError('Promo code applied but no discount was applied. Please verify the promo code terms.');
        setPromoApplied(false);
        setPromoLoading(false);
        return;
      }
    } else {
      setPromoError('Promo code applied but no discount was applied. Please verify the promo code terms.');
      setPromoApplied(false);
      setPromoLoading(false);
      return;
    }
  }
  
  setJustApplied(true);
  setPromoApplied(true);
  setPromoError('');
  setPromoInput(trimmedPromoCode);
  setIsPromoOpen(false);
  
  // Store promo info to apply when rooms are added later
  dispatch({
    type: 'UPDATE_SEARCH_STATE',
    payload: { 
      promo_code: trimmedPromoCode,
      promo_from_widget: false
    },
  });
  
  dispatch({
    type: 'PERFORM_SEARCH',
    payload: {
      hotelId: state.searchState.hotelId,
      checkin: state.searchState.checkin,
      checkout: state.searchState.checkout,
      rooms: state.rooms.length,
      guests: state.searchState.guestCount,
      promo_code: trimmedPromoCode,
      apiResponse: hotelData,
    },
  });
  
  // CRITICAL: Store promo info in a special flag to apply to cart when rooms are added
  dispatch({
    type: 'APPLY_WIDGET_PROMO',
    payload: {
      discountAmount: totalDiscount,
      promoCode: trimmedPromoCode,
      discountedSubtotal: hotelData?.discounted_subtotal || 0,
      discountedTaxes: hotelData?.discounted_taxes || 0,
      discountedTotal: hotelData?.discounted_total || 0,
    },
  });
  
  console.log('✅ Manual promo applied successfully (no cart items), discount:', totalDiscount);
  
  setTimeout(() => {
    setJustApplied(false);
  }, 500);
  } else {
    setPromoError(promoCodeInfo?.message || 'Promo code could not be applied');
    setPromoApplied(false);
  }
  }
    
  } catch (err) {
    console.error('Promo validation error:', err);
    setPromoError('Could not validate code. Please try again.');
    setPromoApplied(false);
  } finally {
    setPromoLoading(false);
  }
};

const calculateNights = useCallback(() => {
  if (!state.searchState.checkin || !state.searchState.checkout) return 0;
  const checkIn = new Date(state.searchState.checkin);
  const checkOut = new Date(state.searchState.checkout);
  const diffTime = Math.abs(checkOut - checkIn);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}, [state.searchState.checkin, state.searchState.checkout]);

// const handleApplyPromo = async () => {
//   if (!promoInput.trim()) return;

//   if (!state.searchState.hotelId) {
//     setPromoError('Please select a hotel first.');
//     return;
//   }
//   if (!state.searchState.checkin || !state.searchState.checkout) {
//     setPromoError('Please select check-in and check-out dates first.');
//     return;
//   }

//   setPromoLoading(true);
//   setPromoError('');

//   try {
//     const formatDate = (d) => {
//       const date = new Date(d);
//       return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
//     };

//     const searchParams = {
//       startDate: formatDate(state.searchState.checkin),
//       endDate: formatDate(state.searchState.checkout),
//       rooms: state.rooms.length,
//       guests: state.searchState.guestCount,
//       roomsData: state.rooms,
//       promo_code: promoInput.trim(),
//     };

//     const hotelData = await apiService.getHotelData(
//       state.searchState.hotelId,
//       searchParams
//     );

//     const promoCodeInfo = hotelData?.promo_code_info;
    
//     console.log('📦 Manual promo validation response:', promoCodeInfo);
    
//     if (!promoCodeInfo || promoCodeInfo.is_valid === false) {
//       setPromoError(promoCodeInfo?.message || 'Invalid promo code');
//       setPromoApplied(false);
//       setPromoLoading(false);
//       return;
//     }

//     if (promoCodeInfo.applied === true) {
//       // Calculate total discount from rate plans
//       let totalDiscount = 0;
//       hotelData?.categories?.forEach(category => {
//         category?.rate_plans?.forEach(plan => {
//           if (plan?.offer?.is_promo_code === true && plan?.offer?.promo_code === promoInput.trim()) {
//             const saving = (plan.originalPrice || 0) - (plan.price || 0);
//             if (saving > 0) totalDiscount += saving;
//           }
//         });
//       });

//       // Set justApplied flag to prevent immediate clearing
//       setJustApplied(true);
      
//       // Update state first
//       setPromoApplied(true);
//       setPromoError('');
//       setPromoInput(promoInput.trim());
      
//       // Close the promo popup
//       setIsPromoOpen(false);
      
//       // Update context
//       dispatch({
//         type: 'UPDATE_SEARCH_STATE',
//         payload: { 
//           promo_code: promoInput.trim(),
//           promo_from_widget: false
//         },
//       });

//       dispatch({
//         type: 'PERFORM_SEARCH',
//         payload: {
//           hotelId: state.searchState.hotelId,
//           checkin: state.searchState.checkin,
//           checkout: state.searchState.checkout,
//           rooms: state.rooms.length,
//           guests: state.searchState.guestCount,
//           promo_code: promoInput.trim(),
//           apiResponse: hotelData,
//         },
//       });

//       dispatch({
//         type: 'APPLY_WIDGET_PROMO',
//         payload: {
//           discountAmount: totalDiscount,
//           promoCode: promoInput.trim(),
//         },
//       });
      
//       console.log('✅ Manual promo applied successfully');
      
//       // Reset justApplied flag after a short delay
//       setTimeout(() => {
//         setJustApplied(false);
//       }, 500);
      
//     } else {
//       setPromoError(promoCodeInfo?.message || 'Promo code could not be applied');
//       setPromoApplied(false);
//     }

//   } catch (err) {
//     console.error('Promo validation error:', err);
//     setPromoError('Could not validate code. Please try again.');
//     setPromoApplied(false);
//   } finally {
//     setPromoLoading(false);
//   }
// };
// Debug useEffect to track promo state changes
useEffect(() => {
  console.log('🔍 Promo State Debug:', {
    promoApplied,
    promoInput,
    statePromoCode: state.searchState.promo_code,
    promo_from_widget: state.searchState.promo_from_widget,
    widgetPromoApplied,
    promoError
  });
}, [promoApplied, promoInput, state.searchState.promo_code, state.searchState.promo_from_widget, widgetPromoApplied, promoError]);

// const handleClearPromo = (e) => {
//   if (e) e.stopPropagation();
  
//   // Clear cart and discounts
//   dispatch({ type: 'CLEAR_CART' });
//   dispatch({ type: 'CLEAR_DISCOUNT' });
  
//   setJustApplied(false);
//   dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
//   setPromoInput('');
//   setPromoApplied(false);
//   setPromoError('');
//   setWidgetPromoApplied(false);
//   setIsPromoOpen(false);
//   // ADD THIS LINE - Notify sidebar promo is cleared
//   dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
// };


// const handleClearPromo = (e) => {
//   if (e) e.stopPropagation();
  
//   console.log('🗑️ Clearing promo and restoring original cart state');
  
//   // Try to restore from saved original state
//   const savedOriginalState = localStorage.getItem('original_cart_state');
  
//   if (savedOriginalState) {
//     try {
//       const originalState = JSON.parse(savedOriginalState);
      
//       console.log('🔄 Restoring original cart state:', originalState);
      
//       // Restore original cart and totals exactly as they were
//       dispatch({
//         type: 'RESTORE_ORIGINAL_CART_STATE',
//         payload: originalState
//       });
      
//       // Clear the saved state
//       localStorage.removeItem('original_cart_state');
      
//       // Clear all promo state
//       setJustApplied(false);
//       setPromoApplied(false);
//       setPromoInput('');
//       setPromoError('');
//       setWidgetPromoApplied(false);
//       setIsPromoOpen(false);
      
//       // Clear promo from search state
//       dispatch({ 
//         type: 'UPDATE_SEARCH_STATE', 
//         payload: { 
//           promo_code: '', 
//           promo_from_widget: false 
//         } 
//       });
      
//       // IMPORTANT: Clear sidebar promo and force UI update
//       dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
      
//       // Also clear the applied promo code in sidebar's local state via context
//       dispatch({ type: 'CLEAR_PROMO' }); // Add this action if you have it
      
//       // Clear localStorage promo state
//       localStorage.removeItem('booking_promo_state');
      
//       console.log('✅ Original cart restored successfully');
//       return;
//     } catch (error) {
//       console.error('Error restoring original state:', error);
//     }
//   }
  
//   // Fallback: If no saved state, manually restore
//   console.log('⚠️ No saved original state, using fallback restore');
  
//   if (state.cart && state.cart.length > 0) {
//     const restoredCart = state.cart.map(item => {
//       // Get the original stored values from the item itself
//       const originalRoomPrice = item.originalRoomPrice || item.roomPrice;
//       const originalTaxData = item.originalTaxData || item.taxData;
      
//       // Calculate original total for stay
//       const checkIn = new Date(state.searchState.checkin);
//       const checkOut = new Date(state.searchState.checkout);
//       const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
//       const originalTotalForStay = originalRoomPrice * nights;
      
//       return {
//         ...item,
//         roomPrice: originalRoomPrice,
//         discountedPricePerNight: originalRoomPrice,
//         originalPrice: originalRoomPrice,
//         discountAmount: 0,
//         hasPromoApplied: false,
//         promoCodeApplied: null,
//         taxData: originalTaxData || {
//           total_tax_amount: originalTotalForStay * 0.18,
//           total_cgst_amount_after_tax: (originalTotalForStay * 0.18) / 2,
//           total_sgst_amount_after_tax: (originalTotalForStay * 0.18) / 2,
//           total_cess_amount: 0,
//         },
//         ratePlan: item.ratePlan ? {
//           ...item.ratePlan,
//           offer: item.ratePlan.offer ? {
//             ...item.ratePlan.offer,
//             overridden_by_promo: false,
//             is_overridden: false
//           } : null
//         } : null,
//         offerName: item.offerName ? item.offerName.replace(' (overridden by promo)', '') : item.offerName,
//         originalRoomPrice: undefined, // Clean up
//         originalTaxData: undefined, // Clean up
//       };
//     });
    
//     const originalSubtotal = restoredCart.reduce((sum, item) => {
//       const checkIn = new Date(state.searchState.checkin);
//       const checkOut = new Date(state.searchState.checkout);
//       const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
//       return sum + (item.roomPrice * nights);
//     }, 0);
    
//     const originalTaxes = restoredCart.reduce((sum, item) => sum + (item.taxData?.total_tax_amount || 0), 0);
//     const originalTotal = originalSubtotal + originalTaxes;
    
//     dispatch({
//       type: 'RESTORE_ORIGINAL_CART_STATE',
//       payload: {
//         cart: restoredCart,
//         subtotal: originalSubtotal,
//         taxes: originalTaxes,
//         total: originalTotal,
//         finalTotal: originalTotal,
//         hasDiscount: false,
//         discountAmount: 0,
//         promoCode: null
//       }
//     });
//   }
  
  
//   // Clear all promo state
//   setJustApplied(false);
//   setPromoApplied(false);
//   setPromoInput('');
//   setPromoError('');
//   setWidgetPromoApplied(false);
//   setIsPromoOpen(false);
  
//   dispatch({ 
//     type: 'UPDATE_SEARCH_STATE', 
//     payload: { 
//       promo_code: '', 
//       promo_from_widget: false 
//     } 
//   });
  
//   dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
//   dispatch({ type: 'CLEAR_PROMO' }); // Add this
//   localStorage.removeItem('booking_promo_state');
//   localStorage.removeItem('original_cart_state');
// };

// const handleClearPromo = async (e) => {
//   if (e) e.stopPropagation();
  
//   console.log('🗑️ Clearing promo and restoring original cart state');
  
//   // CRITICAL: Clear persisted stackable info to force fresh fetch
//   localStorage.removeItem('hotel_stackable_info');
  
//   // Reset stackable flags on all cart items
//   const resetCart = state.cart.map(item => ({
//     ...item,
//     isStackableApplied: false,
//     offerOverriddenByPromo: false,
//     hasPromoApplied: false,
//     promoCodeApplied: null,
//     ratePlan: item.ratePlan ? {
//       ...item.ratePlan,
//       offer: item.ratePlan.offer ? {
//         ...item.ratePlan.offer,
//         overridden_by_promo: false,
//         is_overridden: false,
//         is_stackable_applied: false
//       } : null
//     } : null,
//     offerName: item.offerName?.replace(' (overridden by promo)', '') || 
//                item.offerName?.replace(' (overridden by promo)', '') || 
//                item.offerName,
//     roomPrice: item.originalRoomPrice || item.roomPrice,
//     discountedPricePerNight: null,
//     discountAmount: 0,
//     taxData: item.originalTaxData || item.taxData
//   }));
  
//   // Try to restore from saved original state
//   const savedOriginalState = localStorage.getItem('original_cart_state');
  
//   if (savedOriginalState) {
//     try {
//       const originalState = JSON.parse(savedOriginalState);
      
//       dispatch({
//         type: 'RESTORE_ORIGINAL_CART_STATE',
//         payload: originalState
//       });
      
//       localStorage.removeItem('original_cart_state');
      
//       // Clear all promo state
//       setJustApplied(false);
//       setPromoApplied(false);
//       setPromoInput('');
//       setPromoError('');
//       setWidgetPromoApplied(false);
//       setIsPromoOpen(false);
      
//       // Clear promo from search state
//       dispatch({ 
//         type: 'UPDATE_SEARCH_STATE', 
//         payload: { 
//           promo_code: '', 
//           promo_from_widget: false 
//         } 
//       });
      
//       // Clear sidebar promo
//       dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
//       dispatch({ type: 'CLEAR_PROMO' });
      
//       // Clear localStorage promo state
//       localStorage.removeItem('booking_promo_state');
      
//       // CRITICAL: Force refresh of stackable info by fetching fresh data
//       await fetchFreshStackableInfo();
      
//       console.log('✅ Original cart restored successfully');
//       return;
//     } catch (error) {
//       console.error('Error restoring original state:', error);
//     }
//   }
  
//   // Fallback: Update cart with reset values
//   dispatch({ type: 'UPDATE_CART', payload: resetCart });
  
//   // Clear all promo state
//   setJustApplied(false);
//   setPromoApplied(false);
//   setPromoInput('');
//   setPromoError('');
//   setWidgetPromoApplied(false);
//   setIsPromoOpen(false);
  
//   dispatch({ 
//     type: 'UPDATE_SEARCH_STATE', 
//     payload: { 
//       promo_code: '', 
//       promo_from_widget: false 
//     } 
//   });
  
//   dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
//   dispatch({ type: 'CLEAR_PROMO' });
//   localStorage.removeItem('booking_promo_state');
//   localStorage.removeItem('original_cart_state');
  
//   // CRITICAL: Force refresh of stackable info by fetching fresh data
//   await fetchFreshStackableInfo();
// };

const handleClearPromo = async (e) => {
  if (e) e.stopPropagation();
  
  console.log('🗑️ Clearing promo');
  
   // Clear discount_id when clearing promo
  setCurrentPromoDiscountId(null);

  // CRITICAL: Clear persisted stackable info to force fresh fetch
  localStorage.removeItem('hotel_stackable_info');
  
  // Check if promo was applied BEFORE any cart items were added
  if (promoAppliedBeforeCart) {
    console.log('🗑️ Promo was applied before cart items - clearing cart');
    // Clear the entire cart since items were added after promo was applied
    dispatch({ type: 'CLEAR_CART' });
    setPromoAppliedBeforeCart(false);
  } else {
    // Promo was applied after cart items existed - just remove promo flags from items
    console.log('📝 Promo was applied to existing cart items - preserving cart');
    const resetCart = state.cart.map(item => ({
      ...item,
      isStackableApplied: false,
      offerOverriddenByPromo: false,
      hasPromoApplied: false,
      promoCodeApplied: null,
      ratePlan: item.ratePlan ? {
        ...item.ratePlan,
        offer: item.ratePlan.offer ? {
          ...item.ratePlan.offer,
          overridden_by_promo: false,
          is_overridden: false,
          is_stackable_applied: false
        } : null
      } : null,
      offerName: item.offerName?.replace(' (overridden by promo)', '') || 
                 item.offerName?.replace(' (overridden by promo)', '') || 
                 item.offerName,
      roomPrice: item.originalRoomPrice || item.roomPrice,
      discountedPricePerNight: null,
      discountAmount: 0,
      taxData: item.originalTaxData || item.taxData
    }));
    
    dispatch({ type: 'UPDATE_CART', payload: resetCart });
  }
  
  // Clear all promo state
  setJustApplied(false);
  setPromoApplied(false);
  setPromoInput('');
  setPromoError('');
  setWidgetPromoApplied(false);
  setIsPromoOpen(false);
  
  // Clear promo from search state
  dispatch({ 
    type: 'UPDATE_SEARCH_STATE', 
    payload: { 
      promo_code: '', 
      promo_from_widget: false 
    } 
  });
  
  // Clear sidebar promo
  dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
  dispatch({ type: 'CLEAR_PROMO' });
  
  // Clear localStorage promo state
  localStorage.removeItem('booking_promo_state');
  localStorage.removeItem('original_cart_state');
  
  // CRITICAL: Force refresh of stackable info by fetching fresh data
  await fetchFreshStackableInfo();
};

// Add this helper function to fetch fresh stackable info
const fetchFreshStackableInfo = async () => {
  if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
    return;
  }
  
  try {
    const formatDate = (d) => {
      const date = new Date(d);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    
    const searchParams = {
      startDate: formatDate(state.searchState.checkin),
      endDate: formatDate(state.searchState.checkout),
      rooms: state.rooms.length,
      guests: state.searchState.guestCount,
      roomsData: state.rooms,
      promo_code: null,
    };
    
    const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
    const freshStackableInfo = {
      is_stackable: hotelData?.is_stackable || false,
      available_promo_codes: hotelData?.available_promo_codes || []
    };
    
    // Also capture any discount_id from the fresh data
    if (hotelData?.discount_id) {
      setCurrentPromoDiscountId(hotelData.discount_id);
    }
    
    setHotelStackableInfo(freshStackableInfo);
    
    localStorage.setItem('hotel_stackable_info', JSON.stringify({
      ...freshStackableInfo,
      timestamp: Date.now(),
      hotelId: state.searchState.hotelId,
      discount_id: hotelData?.discount_id || null
    }));
    
    console.log('✅ Fetched fresh stackable info:', freshStackableInfo);
  } catch (error) {
    console.error('Error fetching fresh stackable info:', error);
  }
};
  // --- HANDLERS ---
  const filteredHotels = hotels.filter(hotel =>
    hotel.name.toLowerCase().includes(hotelSearch.toLowerCase())
  );
const handleHotelSelect = async (hotel) => {
  // Clear existing state
  dispatch({ type: 'CLEAR_CART' });
  dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
  dispatch({ type: 'CLEAR_DISCOUNT' });
  setPromoInput('');
  setPromoApplied(false);
  setPromoError('');
  setWidgetPromoApplied(false);

  dispatch({ 
    type: 'UPDATE_SEARCH_STATE', 
    payload: { 
      hotel: hotel.name, 
      hotelId: hotel.id,
      searchPerformed: false
    } 
  });

  // Clear localStorage stackable info when hotel changes
  localStorage.removeItem('hotel_stackable_info');

  // Fetch hotel data to get stackable information
  if (state.searchState.checkin && state.searchState.checkout) {
    try {
      const formatDate = (d) => {
        const date = new Date(d);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      
      const searchParams = {
        startDate: formatDate(state.searchState.checkin),
        endDate: formatDate(state.searchState.checkout),
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
        promo_code: null,
      };
      
      const hotelData = await apiService.getHotelData(hotel.id, searchParams);
      const stackableInfoData = {
        is_stackable: hotelData?.is_stackable || false,
        available_promo_codes: hotelData?.available_promo_codes || []
      };
      setHotelStackableInfo(stackableInfoData);

      // Store in localStorage
      localStorage.setItem('hotel_stackable_info', JSON.stringify({
        ...stackableInfoData,
        timestamp: Date.now(),
        hotelId: hotel.id
      }));
    } catch (error) {
      console.error('Error fetching hotel stackable info:', error);
    }
  }

  // Rest of your existing code...
  try {
    const currentSession = JSON.parse(sessionStorage.getItem('bookingSession') || '{}');
    currentSession.searchState = {
      ...currentSession.searchState,
      hotel: hotel.name,
      hotelId: hotel.id,
      searchPerformed: false,
      promo_code: '',
      promo_from_widget: false
    };
    currentSession.cart = [];
    currentSession.subtotal = 0;
    currentSession.total = 0;
    currentSession.finalTotal = 0;
    currentSession.taxes = 0;
    currentSession.hasDiscount = false;
    currentSession.discountAmount = 0;
    currentSession.promoCode = '';
    currentSession.offerDiscount = 0;
    
    sessionStorage.setItem('bookingSession', JSON.stringify(currentSession));
  } catch (error) {
    console.error('Error updating session storage:', error);
  }

  setHotelDropdownOpen(false);
  setIsDestinationOpen(false);
  setHotelSearch('');
};


   const handleGuestsUpdate = (rooms) => {
    dispatch({ type: 'CLEAR_CART' });
     dispatch({ type: 'CLEAR_DISCOUNT' });
    dispatch({ type: 'UPDATE_ROOMS', payload: rooms });
    const totalAdults = rooms.reduce((acc, r) => acc + r.adults, 0);
    const totalChildren = rooms.reduce((acc, r) => acc + r.children, 0);
    const totalGuests = totalAdults + totalChildren; 
    const guestsText = `${rooms.length} Room${rooms.length !== 1 ? 's' : ''}, ${totalGuests} Guest${totalGuests !== 1 ? 's' : ''}`;
    
    const rooms_guests = rooms.map((room, index) => ({
      room_no: index + 1, 
      no_of_adults: room.adults, 
      no_of_children: room.children,
      child_ages: room.childAges || [] 
    }));

    dispatch({
      type: 'UPDATE_SEARCH_STATE',
      payload: { guestsText, guestCount: totalGuests, room_guest: rooms_guests }
    });
    setGuestsDropdownOpen(false);
  };

const handleSearch = async () => {
  console.log('🔍 handleSearch called');
  
  if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
    alert('Please select hotel, check-in, and check-out dates');
    return;
  }
  
  const formatDate = (d) => {
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const searchParams = {
    startDate: formatDate(state.searchState.checkin),
    endDate: formatDate(state.searchState.checkout),
    rooms: state.rooms.length,
    guests: state.searchState.guestCount,
    roomsData: state.rooms,
    promo_code: state.searchState.promo_code ? state.searchState.promo_code.toUpperCase() : null, 
  };
  
  try {
    setSearchLoading(true);
    const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
    
    const stackableInfo = {
      is_stackable: hotelData?.is_stackable || false,
      available_promo_codes: hotelData?.available_promo_codes || []
    };
    
    setHotelStackableInfo(stackableInfo);
    
    // Store in localStorage for persistence
    localStorage.setItem('hotel_stackable_info', JSON.stringify({
      ...stackableInfo,
      timestamp: Date.now(),
      hotelId: state.searchState.hotelId
    }));
    
    // Store is_stackable flag from response
    const isStackable = hotelData?.is_stackable || false;
    const availablePromoCodes = hotelData?.available_promo_codes || [];
    
    const promoCodeInfo = hotelData?.promo_code_info;
    
    if (state.searchState.promo_code && promoCodeInfo) {
      // Check if the promo is stackable
      const promoStackable = availablePromoCodes.some(
        p => p.code === state.searchState.promo_code && p.is_stackable === 1
      ) || isStackable;
      
      if (promoCodeInfo.is_valid === true && promoCodeInfo.applied === true) {
        // Calculate discount
        let totalDiscount = 0;
        hotelData?.categories?.forEach(category => {
          category?.rate_plans?.forEach(plan => {
            if (plan?.offer?.is_promo_code === true && plan?.offer?.promo_code === state.searchState.promo_code) {
              const saving = (plan.originalPrice || 0) - (plan.price || 0);
              if (saving > 0) totalDiscount += saving;
            }
          });
        });
        
        dispatch({
          type: 'APPLY_WIDGET_PROMO',
          payload: {
            discountAmount: totalDiscount,
            promoCode: state.searchState.promo_code,
            isStackable: promoStackable
          },
        });
        setPromoApplied(true);
        setPromoError('');
      } else {
        setPromoError(promoCodeInfo?.message || 'Invalid promo code');
        setPromoApplied(false);
      }
    }
    
    dispatch({
      type: 'PERFORM_SEARCH',
      payload: {
        hotelId: state.searchState.hotelId,
        checkin: state.searchState.checkin,
        checkout: state.searchState.checkout,
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        promo_code: state.searchState.promo_code || null,
        apiResponse: hotelData,
        is_stackable: isStackable,
        available_promo_codes: availablePromoCodes
      }
    });
    
    setIsMobileSearchOpen(false);

  } catch (error) {
    console.error(error);
    alert('Search failed');
  } finally {
    setSearchLoading(false);
  }
};

  // Helper function to check if selected range has unavailable dates
 const hasUnavailableDatesInSelectedRange = useCallback(() => {
    if (!state.searchState.checkin || !state.searchState.checkout || Object.keys(inventoryStatus).length === 0) {
      return false;
    }
    
    const formatDateForCheck = (date) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    
    const start = new Date(state.searchState.checkin);
    const end = new Date(state.searchState.checkout);
    let current = new Date(start);
    
    while (current <= end) {
      const dateStr = formatDateForCheck(current);
      const status = inventoryStatus[dateStr];
      if (status && !status.has_available_inventory) {
        return true;
      }
      current.setDate(current.getDate() + 1);
    }
    return false;
  }, [state.searchState.checkin, state.searchState.checkout, inventoryStatus]);

  return (
    <>
     {/* ========================================================== */}
{/* DESKTOP VIEW - FIXED FLEX LAYOUT                          */}
{/* ========================================================== */}
<div className="hidden md:block w-full bg-white border-b border-gray-200 shadow-sm">
  <div className="container mx-auto px-6 lg:px-8 py-4">
    {/* flex-row ensures items sit side-by-side; items-end aligns labels and boxes */}
    <div className="flex flex-row items-end justify-start gap-4">
      
      {/* 1. Hotel Selector - Set a fixed width to prevent shrinking */}
{/* 1. Hotel Selector */}
<div className="w-[280px] relative group">
  <button
    type="button"
    onClick={() => setHotelDropdownOpen(!hotelDropdownOpen)}
    className={`w-full h-12 text-left px-4 border rounded-xl transition-all flex items-center justify-between bg-white shadow-sm ${
      hotelDropdownOpen ? 'border-brand-accent ring-4 ring-brand-accent/5' : 'border-gray-200 hover:border-gray-300'
    }`}
  >
    <div className="flex items-center gap-3 overflow-hidden">
      <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
     <div className="flex flex-col min-w-0"> 
  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">
    Hotel
  </span>
  <span className="truncate font-bold text-gray-900 text-[14px] leading-tight">
    {state.searchState.hotel || 'Select Hotel'}
  </span>
</div>
    </div>
    <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${hotelDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/>
    </svg>
  </button>
        
        {hotelDropdownOpen && (
          <div className="absolute top-full left-0 w-full z-50 bg-white border border-gray-100 rounded-2xl shadow-2xl mt-2 p-2 animate-in fade-in zoom-in-95 duration-200">
            <div className="relative mb-2">
              <input
                type="text"
                value={hotelSearch}
                onChange={(e) => setHotelSearch(e.target.value)}
                placeholder="Where are you going?"
                className="w-full h-10 pl-9 pr-4 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-brand-accent/20"
                autoFocus
              />
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {filteredHotels.map((hotel) => (
                <div 
                  key={hotel.id} 
                  className="p-3 text-sm font-medium text-gray-700 cursor-pointer hover:bg-brand-accent/5 hover:text-brand-accent rounded-lg transition-colors" 
                  onClick={() => handleHotelSelect(hotel)}
                >
                  {hotel.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0">
        <CustomDatePicker onInventoryStatusChange={setInventoryStatus} />
      </div>

      {hasUnavailableDatesInSelectedRange() && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2 hidden">
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Some selected dates have no available rooms</span>
        </div>
      )}
      {/* 3. Rooms & Guests - We give this a flexible width with a minimum */}
      <div className="flex-grow max-w-[340px]">
        <GuestsSelector
          isOpen={guestsDropdownOpen}
          onToggle={() => setGuestsDropdownOpen(!guestsDropdownOpen)}
          onUpdate={handleGuestsUpdate}
          rooms={state.rooms}
        />
      </div>

{/* 5. Promo Code — Desktop */}
<div className="flex-shrink-0 relative" ref={promoRef}>
    <div className="relative group">
    <button
      type="button"
      onClick={() => {
        // Allow opening if: not applied, AND (no other offers OR promo is stackable OR global stackable)
        const hasOffers = hasOtherOffersApplied();
        const globalStackable = state.searchResults?.is_stackable === true;
        const stackable = isPromoStackable(promoInput);
        
        // CRITICAL: Allow if the current input is a stackable promo
        const isTypingStackable = promoInput && isPromoStackable(promoInput);
        
        if (!promoApplied && (!hasOffers || globalStackable || stackable || isTypingStackable)) {
          setIsPromoOpen(!isPromoOpen);
        } else if (!promoApplied && hasOffers && isTypingStackable) {
          // Allow opening even with offers if the entered code is stackable
          setIsPromoOpen(!isPromoOpen);
        }
      }}
      className={`h-12 px-4 border rounded-xl text-sm transition-all flex items-center gap-2 bg-white whitespace-nowrap ${
        promoApplied
          ? 'border-green-300 bg-green-50 cursor-default'
          : (() => {
              const hasOffers = hasOtherOffersApplied();
              const globalStackable = state.searchResults?.is_stackable === true;
              const stackable = isPromoStackable(promoInput);
              const isTypingStackable = promoInput && isPromoStackable(promoInput);
              
              // If there are offers but the entered code is stackable, show as available
              if (hasOffers && (!globalStackable && !stackable && !isTypingStackable)) {
                return 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed';
              } else if (hasOffers && (globalStackable || stackable || isTypingStackable)) {
                return 'border-blue-300 bg-blue-50 text-blue-600 cursor-pointer hover:bg-blue-100';
              } else {
                return 'border-dashed border-gray-300 text-gray-500 hover:border-brand-accent hover:text-brand-accent font-medium';
              }
            })()
      }`}
    >
      {/* SVG icon */}
      <svg className={`w-4 h-4 shrink-0 ${
        promoApplied ? 'text-green-600' : 
        (hasOtherOffersApplied() && (state.searchResults?.is_stackable === true || isPromoStackable(promoInput) || (promoInput && isPromoStackable(promoInput)))) ? 'text-blue-500' :
        hasOtherOffersApplied() ? 'text-gray-400' : ''
      }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3zM6 6h.008v.008H6V6z"/>
      </svg>

      {promoApplied ? (
        // Applied promo display
        (hasOtherOffersApplied() && !isPromoStackable(state.searchState.promo_code)) ? (
    // Show as error state
    <div className="flex flex-col leading-tight">
      <span className="text-[10px] font-bold text-red-600 uppercase tracking-widest">Promo Unavailable</span>
      <span className="text-[11px] font-bold text-red-500">Cannot combine with selected rooms</span>
    </div>
  ) : (
        <>
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] font-bold text-green-600 uppercase tracking-widest">Promo Applied</span>
            <span className="text-[13px] font-bold text-green-800">{state.searchState.promo_code}</span>
          </div>
          <span
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClearPromo(e);
            }}
            title="Remove promo"
            className="ml-1 w-5 h-5 rounded-full bg-green-200 hover:bg-red-100 flex items-center justify-center transition-colors"
          >
            <svg className="w-3 h-3 text-green-700 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </span>
        </>
  )
      ) : (() => {
          const hasOffers = hasOtherOffersApplied();
          const globalStackable = state.searchResults?.is_stackable === true;
          const stackable = isPromoStackable(promoInput);
          const isTypingStackable = promoInput && isPromoStackable(promoInput);
          
          if (hasOffers && !globalStackable && !stackable && !isTypingStackable) {
            return <span className="text-gray-400">Promo unavailable</span>;
          } else if (hasOffers && (globalStackable || stackable || isTypingStackable)) {
            return <span className="text-blue-600">Stackable promo available</span>;
          } else {
            return 'Have a promo code?';
          }
        })()
        
      }
    </button>

{/* Show warning when promo applied but now invalid due to cart offers */}
{promoApplied && hasOtherOffersApplied() && !isPromoStackable(state.searchState.promo_code) && (
  <div className="absolute top-full left-0 mt-1 w-full bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-600 flex items-center gap-2 z-10">
    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
    <span>Promo cannot be combined with selected room offers</span>
    <button 
      onClick={(e) => {
        e.stopPropagation();
        handleClearPromo(e);
      }}
      className="ml-auto text-red-700 hover:text-red-900 font-medium"
    >
      Remove
    </button>
  </div>
)}

    {/* Tooltip for non-stackable promo with offers */}
    {hasOtherOffersApplied() && !promoApplied && !state.searchResults?.is_stackable && !isPromoStackable(promoInput) && (
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Cannot combine promo with existing offer</span>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
      </div>
    )}

    {/* Tooltip for stackable promo with offers */}
    {hasOtherOffersApplied() && !promoApplied && (state.searchResults?.is_stackable === true || isPromoStackable(promoInput)) && promoInput && (
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-blue-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>Stackable promo - can be combined with existing offer</span>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-900 rotate-45"></div>
      </div>
    )}
  </div>

  {/* Success tooltip when promo is stackable and applied */}
  {hasOtherOffersApplied() && promoApplied && (state.searchResults?.is_stackable === true || isPromoStackable(state.searchState.promo_code)) && (
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-green-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
      <div className="flex items-center gap-2">
        <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>Promo combined with existing offer (stackable)</span>
      </div>
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-green-900 rotate-45"></div>
    </div>
  )}

  {/* Popover - shown when promo not applied, and (no offers OR stackable) */}
  {isPromoOpen && !promoApplied && (!hasOtherOffersApplied() || state.searchResults?.is_stackable === true || isPromoStackable(promoInput)) && (
    <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 p-4 animate-in fade-in zoom-in-95 duration-150" style={{minWidth: '260px'}}>
      {hasOtherOffersApplied() && (state.searchResults?.is_stackable === true || isPromoStackable(promoInput)) && (
        <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-700 flex items-center gap-1">
            <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Stackable promo - will combine with your existing offer
          </p>
        </div>
      )}
      <p className="text-sm font-bold text-gray-800 mb-1">Enter promo code</p>
      <p className="text-xs text-gray-400 mb-3">Code is validated before you search.</p>
      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={promoInput}
          onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
          autoFocus
          disabled={promoLoading}
          className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm font-medium tracking-wider focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent placeholder:normal-case placeholder:tracking-normal disabled:opacity-50"
        />
        <button
          onClick={handleApplyPromo}
          disabled={!promoInput.trim() || promoLoading}
          className="w-full h-10 bg-brand-accent text-white text-sm font-bold rounded-lg hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          {promoLoading
            ? <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
            : 'Apply'
          }
        </button>
      </div>
      {promoError && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd"/>
          </svg>
          {promoError}
        </p>
      )}
    </div>
  )}
</div>
      {/* 4. Optional Search Button (Hidden by default based on your code) */}
      <button onClick={handleSearch} disabled={searchLoading} className="hidden">
        {searchLoading ? 'Searching...' : 'Search'}
      </button>
      
    </div>
  </div>
</div>

      {/* ========================================================== */}
      {/* MOBILE VIEW (Sticky Bar + Modals)                          */}
      {/* ========================================================== */}
      
    {/* 1. Sticky Top Search Bar */}
      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <button 
          onClick={() => setIsMobileSearchOpen(true)}
          className="w-full bg-white text-left px-4 py-3 flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-brand-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">
              {state.searchState.hotel || 'Where are you going?'}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {state.searchState.checkin && state.searchState.checkout 
                ? `${new Date(state.searchState.checkin).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(state.searchState.checkout).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'Add dates'
              } • {state.rooms.length} Room, {state.rooms.reduce((acc, r) => acc + r.adults, 0)} Adult, {state.rooms.reduce((acc, r) => acc + r.children, 0)} Child
            </div>
          </div>
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 2. Main Mobile Modal */}
      {isMobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-gray-50 flex flex-col animate-in slide-in-from-bottom duration-200">
          <div className="bg-white px-4 py-4 flex items-center justify-between border-b border-gray-100 shadow-sm shrink-0">
            <button onClick={() => setIsMobileSearchOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h2 className="font-bold text-lg text-gray-800">Plan your trip</h2>
            <div className="w-8"></div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            
            {/* DESTINATION TRIGGER */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Destination</label>
              <button 
                onClick={() => setIsDestinationOpen(true)} 
                className="w-full flex items-center bg-gray-50 border border-gray-200 rounded-xl h-12 px-3 text-left"
              >
                <svg className="w-5 h-5 text-brand-accent mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
                <span className={`text-base font-semibold ${state.searchState.hotel ? 'text-gray-900' : 'text-gray-400'}`}>
                  {state.searchState.hotel || 'Where to?'}
                </span>
              </button>
            </div>

            {/* Dates */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Dates</label>
              <CustomDatePicker onInventoryStatusChange={setInventoryStatus} />
            </div>

          {/* Rooms & Guests Trigger */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Rooms & Guests</label>
  <button 
    onClick={() => setIsGuestSheetOpen(true)} 
    className="w-full h-12 flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 text-left hover:border-gray-300 transition-all shadow-sm"
  >
    <div className="flex items-center gap-3">
      <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" />
      </svg>
      <span className="font-bold text-gray-900">
        {state.rooms.length} Room{state.rooms.length > 1 ? 's' : ''}, {state.rooms.reduce((acc, r) => acc + r.adults, 0)} Adults, {state.rooms.reduce((acc, r) => acc + r.children, 0)} Children
      </span>
    </div>
    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
    </svg>
  </button>
</div>
  {/* Promo Code — Mobile */}
<div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Promo Code</label>

  {promoApplied ? (
    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl h-12 px-4">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <span className="font-bold text-green-800 text-sm tracking-wider">{state.searchState.promo_code}</span>
        <span className="text-xs text-green-600 font-medium">Applied</span>
      </div>
      <button
        onClick={handleClearPromo}
        className="text-xs font-bold text-red-500 px-2 py-1 hover:bg-red-50 rounded-lg transition-colors"
      >
        Remove
      </button>
    </div>
  ) : isPromoDisabled ? (
    <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl h-12 px-4">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex flex-col">
          <span className="font-bold text-amber-800 text-sm">Promo Unavailable</span>
          <span className="text-xs text-amber-600">Cannot combine with {offerDescription}</span>
        </div>
      </div>
    </div>
  ) : (
    <>
      <div className="flex gap-2">
        <input
          type="text"
          value={promoInput}
          onChange={(e) => { setPromoInput(e.target.value); setPromoError(''); }}
          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
          placeholder="Enter promo code"
          disabled={promoLoading}
          className="flex-1 h-12 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium tracking-wider focus:outline-none focus:ring-2 focus:ring-brand-accent/50 focus:border-brand-accent uppercase placeholder:normal-case placeholder:tracking-normal disabled:opacity-50"
        />
        <button
          onClick={handleApplyPromo}
          disabled={!promoInput.trim() || promoLoading}
          className="h-12 px-5 bg-brand-accent text-white text-sm font-bold rounded-xl hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
        >
          {promoLoading
            ? <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
            : 'Apply'
          }
        </button>
      </div>
      {promoError && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd"/>
          </svg>
          {promoError}
        </p>
      )}
    </>
  )}
</div>

<div className="h-20"></div>
            <div className="h-20"></div>
          </div>

          <div className="bg-white border-t border-gray-200 p-4 shrink-0 pb-safe">
           {/* vansh-search start */}
<button
  onClick={handleSearch}
  disabled={searchLoading}
  className={`w-full h-14 rounded-xl text-white font-bold text-lg tracking-wide flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
    searchLoading
      ? 'bg-brand-accent/50 cursor-not-allowed shadow-none'
      : 'bg-gradient-to-b from-brand-accent to-brand-accent-hover shadow-lg shadow-black/20 hover:shadow-xl hover:brightness-105'
  }`}
>
  {searchLoading ? (
    'Searching...'
  ) : (
    <>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
        <circle cx="11" cy="11" r="7" />
        <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
      </svg>
      Search
    </>
  )}
</button>
{/* vansh-search end */}
          </div>
        </div>
      )}

      {/* 3. DESTINATION FULL SCREEN SHEET */}
      {isDestinationOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-white flex flex-col animate-in slide-in-from-right duration-300">
           <div className="flex items-center gap-2 p-4 border-b border-gray-100 shadow-sm shrink-0">
              <button onClick={() => setIsDestinationOpen(false)} className="p-2 rounded-full hover:bg-gray-100">
                 <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="flex-1 relative">
                 <input 
                    autoFocus
                    type="text"
                    className="w-full h-12 pl-10 pr-4 bg-gray-100 rounded-xl font-semibold text-lg outline-none focus:ring-2 focus:ring-brand-accent/50"
                    placeholder="Search hotel name..."
                    value={hotelSearch}
                    onChange={(e) => setHotelSearch(e.target.value)}
                  />
                 <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </div>
           </div>

           <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-1">
                 {filteredHotels.length > 0 ? filteredHotels.map(h => (
                   <div 
                     key={h.id} 
                     onClick={() => handleHotelSelect(h)}
                     className="flex items-center gap-3 p-4 border-b border-gray-50 active:bg-gray-50 transition-colors"
                   >
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
                      </div>
                      <div>
                          <div className="font-semibold text-gray-900 text-lg">{h.name}</div>
                          <div className="text-sm text-gray-400">Hotel</div>
                      </div>
                   </div>
                 )) : (
                   <div className="text-center py-10 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      No hotels found
                   </div>
                 )}
              </div>
           </div>
        </div>
      )}

      {/* 4. GUEST BOTTOM SHEET */}
      {isGuestSheetOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setIsGuestSheetOpen(false)}></div>
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] animate-in slide-in-from-bottom duration-300">
             <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Select Guests</h3>
                <button onClick={() => setIsGuestSheetOpen(false)} className="p-2 bg-gray-100 rounded-full">
                   <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
             </div>
             <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                <GuestsSelector isOpen={true} onUpdate={handleGuestsUpdate} rooms={state.rooms} isMobile={true} />
                <div className="h-20"></div>
             </div>
             <div className="p-4 bg-white border-t border-gray-200 pb-safe">
                <button onClick={() => setIsGuestSheetOpen(false)} className="w-full h-12 bg-brand-accent text-white font-bold rounded-xl shadow-md">Done</button>
             </div>
          </div>
        </>
      )}
    </>
  );
};

// --- GUESTS COMPONENT ---
const GuestsSelector = ({ isOpen, onToggle, onUpdate, rooms, isMobile }) => {
  const [localRooms, setLocalRooms] = useState([...rooms]);
  const [expandedRoom, setExpandedRoom] = useState(0); // For Desktop: Tracks which room is being edited

  useEffect(() => { setLocalRooms([...rooms]); }, [rooms, isOpen]);

const updateRoom = (index, field, value) => {
  const newRooms = [...localRooms];
  
  // Special logic for updating child count to sync with childAges array
  if (field === 'children') {
    const currentChildren = newRooms[index].children;
    let newAges = newRooms[index].childAges ? [...newRooms[index].childAges] : [];

    if (value > currentChildren) {
      // Increasing: Add default age (0) for new child
      newAges.push(0);
    } else if (value < currentChildren) {
      // Decreasing: Remove last child's age
      newAges.pop();
    }
    
    newRooms[index] = { ...newRooms[index], children: value, childAges: newAges };
  } else {
    // Standard update for adults
    newRooms[index] = { ...newRooms[index], [field]: value };
  }

  setLocalRooms(newRooms);
  if(isMobile) onUpdate(newRooms);
};

const handleAgeChange = (roomIndex, childIndex, age) => {
  const newRooms = [...localRooms];
  const newAges = [...newRooms[roomIndex].childAges];
  newAges[childIndex] = parseInt(age);
  newRooms[roomIndex] = { ...newRooms[roomIndex], childAges: newAges };
  setLocalRooms(newRooms);
  if(isMobile) onUpdate(newRooms);
};

  const addRoom = () => {
  //  if (localRooms.length < 5) {
    // UPDATED: Initialize with childAges array
    const newRooms = [...localRooms, { adults: 2, children: 0, childAges: [] }];
    setLocalRooms(newRooms);
    setExpandedRoom(newRooms.length - 1);
    if(isMobile) onUpdate(newRooms);
  // }
};

  const removeRoom = (index) => {
    if (localRooms.length > 1) {
      const newRooms = localRooms.filter((_, i) => i !== index);
      setLocalRooms(newRooms);
      setExpandedRoom(Math.max(0, index - 1)); // Safely shift focus
      if(isMobile) onUpdate(newRooms);
    }
  };

  const handleApply = () => { onUpdate(localRooms); if(onToggle) onToggle(); };

  const totalAdults = localRooms.reduce((acc, r) => acc + r.adults, 0);
  const totalChildren = localRooms.reduce((acc, r) => acc + r.children, 0);
  const guestsText = `${localRooms.length} Room${localRooms.length !== 1 ? 's' : ''}, ${totalAdults} Adult${totalAdults !== 1 ? 's' : ''}, ${totalChildren} Child${totalChildren !== 1 ? 'ren' : ''}`;

  // === MOBILE RENDER (Stacked, Always Expanded) ===
 if (isMobile) {
    return (
      <div className="space-y-4">
        {localRooms.map((room, index) => (
          <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4 border-b border-gray-50 pb-3">
              <span className="font-bold text-gray-800">Room {index + 1}</span>
              {localRooms.length > 1 && <button onClick={() => removeRoom(index)} className="text-red-500 text-xs font-bold uppercase tracking-wider px-2 py-1 bg-red-50 rounded">Remove</button>}
            </div>
            
            {/* Adults Row */}
            <div className="flex justify-between items-center mb-4">
               <div><div className="font-semibold text-gray-700">Adults</div><div className="text-xs text-gray-400">Ages 13+</div></div>
               <div className="flex items-center gap-3"><CounterBtn onClick={() => updateRoom(index, 'adults', room.adults - 1)} disabled={room.adults <= 1} icon="-" /><span className="w-6 text-center font-bold text-lg text-gray-900">{room.adults}</span><CounterBtn onClick={() => updateRoom(index, 'adults', room.adults + 1)} disabled={room.adults >= 3} icon="+" /></div>
            </div>
            
            {/* Children Row - Updated Max Limit to 2 */}
            <div className="flex justify-between items-center">
               <div><div className="font-semibold text-gray-700">Children</div><div className="text-xs text-gray-400">Ages 0-12</div></div>
               <div className="flex items-center gap-3"><CounterBtn onClick={() => updateRoom(index, 'children', room.children - 1)} disabled={room.children <= 0} icon="-" /><span className="w-6 text-center font-bold text-lg text-gray-900">{room.children}</span><CounterBtn onClick={() => updateRoom(index, 'children', room.children + 1)} disabled={room.children >= 3} icon="+" /></div>
            </div>

            {/* Child Age Dropdowns */}
            {room.children > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-gray-50">
                {Array.from({ length: room.children }).map((_, childIdx) => (
                  <div key={childIdx}>
                    <label className="block text-xs text-gray-500 mb-1">Child {childIdx + 1} Age</label>
                    <select
                      value={room.childAges?.[childIdx] || 0}
                      onChange={(e) => handleAgeChange(index, childIdx, e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-md text-sm bg-white focus:border-brand-accent outline-none"
                    >
                      {[...Array(13).keys()].map(age => (
                        <option key={age} value={age}>{age} years</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <button onClick={addRoom} className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-bold text-sm hover:border-brand-accent hover:text-brand-accent flex items-center justify-center gap-2"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>Add Another Room</button>
      </div>
    );
  }

  // === DESKTOP RENDER (Accordion / Summary Style) ===
  return (
   <div className="relative min-w-[280px]">
    <button 
      type="button" 
      id="guests-button" 
      onClick={onToggle} 
      className={`w-full h-12 text-left px-4 border rounded-xl transition-all flex items-center justify-between bg-white shadow-sm ${
        isOpen ? 'border-brand-accent ring-4 ring-brand-accent/5' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-3 overflow-hidden">
        <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <div className="flex flex-col">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">
            Rooms & Guests
          </span>
          <span className="truncate font-bold text-gray-900 text-[14px] leading-tight">
            {localRooms.length} Room{localRooms.length !== 1 ? 's' : ''}, {totalAdults} Adults, {totalChildren} Children
          </span>
        </div>
      </div>
      <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

      {isOpen && (
        <div className="absolute top-full right-0 w-80 mt-1 bg-white border border-gray-200 rounded-md shadow-xl z-50 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-100">
           <div className="max-h-[300px] overflow-y-auto">
             {localRooms.map((room, index) => {
               const isExpanded = expandedRoom === index;
               
    if (isExpanded) {
                  // --- RENDER ACTIVE EDITING ROOM (DESKTOP) ---
                  return (
                    <div key={index} className="p-4 border-b border-gray-100 bg-white">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-bold text-dark">Room {index + 1}</span>
                        {localRooms.length > 1 && (
                          <button onClick={() => removeRoom(index)} className="text-xs text-red-500 hover:bg-red-50 px-2 py-1 rounded">Remove</button>
                        )}
                      </div>
                      
                      {/* Adults Row */}
                      <div className="flex justify-between items-center mb-3">
                          <div className="text-sm text-gray-700">Adults <span className="text-xs text-gray-400 block">(12+ yrs)</span></div>
                          <div className="flex items-center gap-2">
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50 disabled:opacity-50" onClick={() => updateRoom(index, 'adults', room.adults - 1)} disabled={room.adults <= 1}>-</button>
                            <span className="w-5 text-center font-bold text-gray-800">{room.adults}</span>
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50" onClick={() => updateRoom(index, 'adults', room.adults + 1)} disabled={room.adults >= 3}>+</button>
                          </div>
                      </div>

                      {/* Children Row - LIMIT UPDATED TO 2 */}
                      <div className="flex justify-between items-center">
                          <div className="text-sm text-gray-700">Children <span className="text-xs text-gray-400 block">(0-11 yrs)</span></div>
                          <div className="flex items-center gap-2">
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50 disabled:opacity-50" onClick={() => updateRoom(index, 'children', room.children - 1)} disabled={room.children <= 0}>-</button>
                            <span className="w-5 text-center font-bold text-gray-800">{room.children}</span>
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50" onClick={() => updateRoom(index, 'children', room.children + 1)} disabled={room.children >= 2}>+</button>
                          </div>
                      </div>

                      {/* Child Age Dropdowns (ADDED) */}
                      {room.children > 0 && (
                        <div className="mt-3 grid grid-cols-2 gap-2 pt-2 border-t border-gray-50">
                          {Array.from({ length: room.children }).map((_, childIdx) => (
                            <div key={childIdx}>
                              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Child {childIdx + 1} Age</label>
                              <select
                                value={room.childAges?.[childIdx] || 0}
                                onChange={(e) => handleAgeChange(index, childIdx, e.target.value)}
                                className="w-full p-1.5 border border-gray-200 rounded text-sm bg-white focus:border-brand-accent outline-none cursor-pointer"
                              >
                                {[...Array(13).keys()].map(age => (
                                  <option key={age} value={age}>{age} years</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                } else {
                 return (
                   <div key={index} className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors">
                     <div>
                       <div className="font-bold text-xs text-gray-500 uppercase tracking-wide mb-0.5">Room {index + 1}</div>
                       <div className="text-sm font-semibold text-gray-800">
                         {room.adults} Adults, {room.children} Children
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedRoom(index)} className="text-brand-accent text-sm font-bold px-2 py-1 hover:bg-brand-accent/5 rounded">Edit</button>
                        {localRooms.length > 1 && (
                          <button onClick={() => removeRoom(index)} className="text-gray-400 hover:text-red-500 p-1">
                             <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                     </div>
                   </div>
                 );
               }
             })}
           </div>
           
           <div className="p-3 bg-white border-t border-gray-100 flex justify-between items-center gap-3">
              
                <button onClick={addRoom} className="text-brand-accent text-sm font-bold hover:underline py-2">+ Add Room</button>
               <div />
              <button onClick={handleApply} className="bg-brand-accent-hover text-white px-6 py-2 rounded-md font-bold text-sm shadow hover:bg-brand-accent-hover transition-colors">Apply</button>
           </div>
        </div>
      )}
    </div>
  );
};

const CounterBtn = ({ onClick, disabled, icon }) => (
  <button onClick={onClick} disabled={disabled} className="w-10 h-10 rounded-full border border-gray-200 bg-white text-brand-accent flex items-center justify-center font-bold text-xl shadow-sm disabled:opacity-30 disabled:bg-gray-50 active:scale-90 transition-transform">{icon}</button>
);

export default SearchBar;